import { createClient, type Session, type SupabaseClient } from "@supabase/supabase-js";
import { supabase } from "./client";
import { crossSubdomainStorage } from "./crossSubdomainStorage";
import { consumeRecoveryTokens, issuerForProjectUrl } from "@/lib/recoveryLanding";

// ---------------------------------------------------------------------------
// Two auth providers at once.
//
// Operator accounts are split across two auth projects: an older one that
// existing accounts stay on, and a newer one that later accounts are created
// in. Existing accounts are not moved, because password hashes cannot be
// exported and moving anyone would force a reset. So the front end holds two
// auth clients and has to work out which one a person belongs to.
//
// ⚠️ THERE IS NO DISCRIMINATOR AT SIGN-IN. The obvious one would be the org's
// subdomain, since that is how tenants are addressed everywhere else — but it
// is not known yet at this point. Operators sign in on the shared host, and the
// org is only resolved AFTERWARDS by GET /api/v1/operator/me, which is the call
// that triggers the redirect to the org's own subdomain. So we try the primary
// provider and fall back to the secondary.
//
// ⚠️ An unauthenticated email-to-provider lookup was considered and rejected:
// it would answer "does this address have an account", which is an enumeration
// oracle, and it would buy one round trip for a handful of accounts.
//
// The fallback costs ONE extra failed request, at SIGN-IN ONLY. Nothing per
// request afterwards: once signed in the session belongs to one client and
// getActiveSession finds it. The API does not pay it at all — it routes on the
// token's issuer.
//
// ⚠️ SHIPS DARK. With no primary configured — which is the default — this is
// exactly the previous behaviour: one client, one call, no extra requests, and
// no second client constructed.
//
// ⚠️ `client.ts` is NOT touched. It is generated, and it has already had one
// local change reverted by a regeneration. The primary client is built here,
// beside it, so that cannot happen to this.
// ---------------------------------------------------------------------------

type AuthClient = SupabaseClient;

let cached: AuthClient[] | null = null;

/** Test-only — drop the memoised client list so env changes take effect. */
export function _resetAuthProvidersForTest(): void {
  cached = null;
  claimPromise = null;
}

function buildPrimary(): AuthClient | null {
  const url = import.meta.env.VITE_SUPABASE_PRIMARY_URL;
  const key = import.meta.env.VITE_SUPABASE_PRIMARY_PUBLISHABLE_KEY;
  // Half-configured is treated as unconfigured. Constructing a client against a
  // URL with no key would produce a provider that fails every sign-in, and the
  // fallback would hide it — every login silently one round trip slower, with
  // nothing to indicate why.
  if (!url || !key) return null;

  return createClient(url, key, {
    auth: {
      // ⚠️ Set EXPLICITLY. supabase-js derives a default storage key from the
      // project ref, which is undocumented behaviour we would be betting two
      // concurrent sessions on. A collision would silently mix them, which at
      // worst means one person holding another's session.
      storageKey: "dv-primary-auth-token",
      // ⚠️ MUST stay false while a second provider exists, and the reason is
      // not obvious. A recovery link lands with the tokens in the URL hash and
      // NOTHING in that hash says which project minted them. With detection on
      // for both clients they race to consume the same fragment, and the winner
      // clears it for the other — supabase-js does not verify signatures in the
      // browser, so the wrong client happily stores another project's tokens
      // under its own key. updateUser then posts them to the wrong project's
      // API, which rejects them, and the fragment is already gone so a reload
      // cannot recover. Leaving this false keeps fragment handling exactly
      // where it already is.
      //
      // Recovery links for THIS client are not lost by turning detection off:
      // captureRecoveryLanding reads the issuer off the access token at module
      // load, takes the fragment out of the URL so no client can auto-consume
      // it, and claimRecoverySession below hands the tokens to this client
      // explicitly. See that function.
      detectSessionInUrl: false,
      // Same cookie storage as the legacy client. Operators are redirected from
      // app.digavouch.com to their org's host, and the default per-origin
      // storage drops the session on that hop — which is the entire reason
      // crossSubdomainStorage exists. Keys differ, so the two never collide.
      storage: crossSubdomainStorage,
      persistSession: true,
      autoRefreshToken: true,
    },
  });
}

/**
 * Every configured client, in the order sign-in should try them.
 *
 * Primary first: later accounts are the growing population and must not pay the
 * fallback cost.
 */
export function authClientsInTryOrder(): AuthClient[] {
  if (!cached) {
    const primary = buildPrimary();
    cached = primary ? [primary, supabase] : [supabase];
  }
  return cached;
}

/**
 * Sign in, trying each provider in order.
 *
 * ⚠️ Returns the LAST provider's error, not the first. The primary's "Invalid
 * login credentials" is noise for someone whose account is on the other
 * provider, and surfacing it would show them the wrong provider's message for
 * an ordinary password typo. The last provider tried is the one holding the
 * older accounts, which is where such a person actually lives.
 */
export async function signInAcrossProviders(
  email: string,
  password: string,
): Promise<{ error: string | null }> {
  const clients = authClientsInTryOrder();
  let lastError: string | null = null;

  for (const client of clients) {
    const { error } = await client.auth.signInWithPassword({ email, password });
    if (!error) return { error: null };
    lastError = error.message;
  }
  return { error: lastError };
}

// ---------------------------------------------------------------------------
// Claiming a recovery landing.
//
// A recovery link arrives with its tokens in the URL fragment and no parameter
// saying which project minted them. captureRecoveryLanding reads the issuer off
// the access token at module load and, when the tokens are NOT the original
// client's, takes the fragment out of the URL so no client can auto-consume
// something it cannot validate. That leaves the tokens in hand and nobody
// holding them — this is where they get handed to the right client.
//
// ⚠️ Memoised, so the session is established exactly once however many callers
// ask. Replaying setSession would churn the session and could race the client's
// own refresh.
//
// ⚠️ Awaited INSIDE getActiveSession and getActiveAuthClient rather than left
// to callers. Correctness must not depend on someone remembering to call it
// first: the reset screen reads the session directly, and a null there leaves
// the operator on a page that never becomes ready, with no error.
// ---------------------------------------------------------------------------
let claimPromise: Promise<void> | null = null;

async function claimRecoverySession(): Promise<void> {
  // Consume, not peek: applying the same tokens twice would churn the session
  // and could race the client's own refresh. claimedOnce already guarantees one
  // call, so this is belt and braces on a path that cannot be retried.
  const pending = consumeRecoveryTokens();
  // No landing, or one we could not route by issuer. Either way the original
  // client has already handled it the way it always has.
  if (!pending?.issuer) return;

  const clients = authClientsInTryOrder();
  const primary = clients.length > 1 ? clients[0] : null;
  if (!primary) return;

  // The ONLY case that reaches setSession: tokens minted by the primary
  // project. Anything else was left in the URL on purpose and is already
  // consumed by the client that owns it — claiming it again would change the
  // one path that works.
  const primaryIssuer = issuerForProjectUrl(import.meta.env.VITE_SUPABASE_PRIMARY_URL);
  if (!primaryIssuer || pending.issuer !== primaryIssuer) return;

  await primary.auth.setSession({
    access_token: pending.access_token,
    refresh_token: pending.refresh_token,
  });
}

/** Run the claim at most once. Safe to await from anywhere, any number of times. */
function claimedOnce(): Promise<void> {
  if (!claimPromise) claimPromise = claimRecoverySession().catch(() => undefined);
  return claimPromise;
}

/** The client currently holding a session, or null. Primary wins a tie. */
export async function getActiveAuthClient(): Promise<AuthClient | null> {
  await claimedOnce();
  for (const client of authClientsInTryOrder()) {
    const { data } = await client.auth.getSession();
    if (data.session) return client;
  }
  return null;
}

/** The active session, or null. */
export async function getActiveSession(): Promise<Session | null> {
  await claimedOnce();
  for (const client of authClientsInTryOrder()) {
    const { data } = await client.auth.getSession();
    if (data.session) return data.session;
  }
  return null;
}

/**
 * Sign out of EVERY client.
 *
 * ⚠️ Signing out of only the active one leaves the other's cookie on
 * `.digavouch.com`, so the next getActiveSession resurrects a session the
 * operator believes they ended. Cheap to get wrong, and it fails open.
 */
export async function signOutEverywhere(): Promise<void> {
  // allSettled, not all: the whole point is that EVERY cookie is cleared, and
  // Promise.all abandons the rest the moment one rejects. Callers treat sign-out
  // as best-effort — api.ts awaits this inside its 401 handler without a
  // try/catch, so a rejection here would surface in place of the real 401 and
  // skip the redirect to the login page.
  await Promise.allSettled(authClientsInTryOrder().map((c) => c.auth.signOut()));
}

/**
 * Request a password reset from EVERY provider.
 *
 * The account lives in exactly one, and we cannot tell which: Supabase returns
 * no error for an unknown address by design (anti-enumeration), so asking only
 * one provider would silently no-op for every user of the other, with the UI
 * showing the same reassuring "if an account exists" message either way.
 */
export async function requestPasswordResetEverywhere(
  email: string,
  redirectTo: string,
): Promise<{ error: string | null }> {
  const results = await Promise.allSettled(
    authClientsInTryOrder().map((c) => c.auth.resetPasswordForEmail(email, { redirectTo })),
  );

  const errors = results.map((r) =>
    r.status === "rejected"
      ? String((r.reason as Error)?.message ?? r.reason)
      : (r.value as { error: { message: string } | null }).error?.message ?? null,
  );

  // ⚠️ Only a TOTAL failure is a failure. One provider erroring is expected —
  // an address exists in exactly one of them. But when every provider errors,
  // something real went wrong (the auth mailer is rate-limited, so this is not
  // hypothetical) and reporting success would leave the person waiting for mail
  // that was never sent, with no reason to try again.
  if (errors.length > 0 && errors.every((e) => e !== null)) {
    return { error: errors[errors.length - 1] };
  }
  return { error: null };
}

/**
 * Fire when ANY provider reports a PASSWORD_RECOVERY landing.
 *
 * A recovery deep-link can arrive from either project, depending on which one
 * holds the account. Subscribing to only one would leave the other's operators
 * on a reset screen that never becomes ready.
 *
 * Unlike the session subscription this passes the event straight through, with
 * no re-resolve: PASSWORD_RECOVERY is an edge, not a state, so there is nothing
 * for a second client to contradict.
 */
export function subscribeToRecoveryAcrossProviders(onRecovery: () => void): () => void {
  const subs = authClientsInTryOrder().map(
    (c) =>
      c.auth.onAuthStateChange((event) => {
        if (event === "PASSWORD_RECOVERY") onRecovery();
      }).data.subscription,
  );
  return () => {
    for (const s of subs) s.unsubscribe();
  };
}

/**
 * Subscribe to auth changes across every client, resolved to ONE active session.
 *
 * ⚠️ Re-resolves rather than trusting the event payload. Both clients emit
 * their own INITIAL_SESSION independently, so whichever one does NOT hold the
 * account fires with `null` a beat after the other fires with a real session. A
 * handler that trusted the payload would sign the person straight back out. The
 * sequence guard drops a slow resolution a later event has already superseded.
 */
export function subscribeToActiveSession(
  onSession: (session: Session | null) => void,
): () => void {
  let seq = 0;
  let stopped = false;

  const resolve = () => {
    const mine = ++seq;
    void getActiveSession().then((session) => {
      if (stopped || mine !== seq) return;
      onSession(session);
    });
  };

  const subs = authClientsInTryOrder().map(
    (c) => c.auth.onAuthStateChange(() => resolve()).data.subscription,
  );

  // ⚠️ Resolve ONCE up front rather than waiting for an INITIAL_SESSION event.
  // Callers clear their loading flag from this callback, and a caller that
  // never hears from us renders nothing at all — a blank page, no error. Do not
  // remove this on the grounds that the library "fires immediately": that is an
  // assumption about the library, not something this function guarantees.
  resolve();

  return () => {
    stopped = true;
    for (const s of subs) s.unsubscribe();
  };
}
